/**
 * Header Component
 *
 * A generic header section that displays the logo and provides slots for actions.
 */
import Link from 'next/link';
import React from 'react';

import { Logo } from '@ui/components/common/logo/Logo';

export interface HeaderProps {
  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Optional content to render on the left side, next to the logo.
   */
  leftActions?: React.ReactNode;

  /**
   * Optional content to render on the right side.
   */
  rightActions?: React.ReactNode;

  /**
   * URL for the logo link. Defaults to '/'.
   */
  logoHref?: string;
}

/**
 * Header component for the website
 *
 * @example
 * ```tsx
 * <Header rightActions={<Button label="Action" />} />
 * ```
 */
export const Header: React.FC<HeaderProps> = ({ className = '', leftActions, rightActions, logoHref = '/' }) => {
  return (
    <>
      <header
        className={`w-full bg-white border-b border-neutral-200 fixed top-0 z-40 pt-[env(safe-area-inset-top)] ${className}`}
      >
        <div className="layout-bounds py-3 xs:py-2 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link href={logoHref} aria-label="TopTopTime — home">
              <div className="h-10 xs:h-12 relative overflow-hidden">
                <Logo variant="full" />
              </div>
            </Link>
            {leftActions}
          </div>
          <div className="flex items-center gap-0 md:gap-4">{rightActions}</div>
        </div>
      </header>
      {/* Spacer div to prevent content from going under fixed header.
          Grows by the iOS safe-area inset when the app runs in standalone
          PWA mode so the page never starts under the notch / status bar. */}
      <div className="h-[calc(65px+env(safe-area-inset-top))]" aria-hidden="true" />
    </>
  );
};
